import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Info, Play, Zap } from "lucide-react";
import ReadinessReport from "@/components/ReadinessReport";
import ConflictReport from "@/components/ConflictReport";
import RecommendationCard from "@/components/RecommendationCard";
import ConfidenceBar from "@/components/ConfidenceBar";
import CoverageSimulationTab from "@/pages/CoverageSimulationTab";
import LeaveSimulationTab from "@/pages/LeaveSimulationTab";
import FairnessSimulationTab from "@/pages/FairnessSimulationTab";
import SimulationHub from "@/pages/SimulationHub";

// added "Simulation hub" as the first/default tab.

const SURFACES = [
  { value: "hub",          label: "Simulation hub" },
  { value: "overview",     label: "Summary" },
  { value: "coverage_sim", label: "Coverage simulation" },
  { value: "leave_sim",    label: "Leave simulation" },
  { value: "fairness_sim", label: "Fairness check" },
  { value: "rota_day",     label: "Roster check" },
  { value: "leave",        label: "Leave check" },
  { value: "eligibility",  label: "Employee fit" },
  { value: "policy",       label: "Policy check" },
  { value: "history",      label: "Previous checks" },
];

function today() { return new Date().toISOString().slice(0, 10); }
function inDays(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function AutomationPreviewPage({ embedded = false }) {
  const [tab, setTab] = useState(() => {
    const h = (window.location.hash || "").replace("#", "");
    return SURFACES.some((s) => s.value === h) ? h : "hub";
  });
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/employees"), api.get("/teams")])
      .then(([e, t]) => { setEmployees(e.data); setTeams(t.data); })
      .catch(() => { /* ignore */ });
  }, []);

  return (
    <div>
      {!embedded && (
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            Tools
            <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]"
              data-testid="auto-advanced-badge">Advanced</span>
          </div>
          <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
            <Zap className="h-7 w-7 text-[#2563EB]" />
            {tab === "hub" ? "Automation hub" : "Automation insights"}
          </h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            {tab === "hub"
              ? "One place to see how Coverage, Assignment, Leave and Fairness are looking across your team. All four are read-only insights, nothing here changes your rota, leave or assignments."
              : "See what NextRota has noticed about your setup, possible understaffed shifts, employees scheduled outside their skills, certifications about to expire, and more. This page is read-only; you decide what to act on."}
          </p>
        </div>
      </div>
      )}

      {tab !== "hub" && (
        <>
          <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
            data-testid="auto-helper-card">
            <div className="text-sm font-semibold text-[#0B1A36] mb-1">What is Automation Insights?</div>
            <p className="text-xs text-[#1F2A44] leading-relaxed">
              Automation Insights looks at your saved setup, employees, skills, rules, policies and
              KPIs, and surfaces anything worth your attention. Today it&apos;s a guide only; future
              automation will use the same signals to suggest decisions. Nothing here ever changes
              your rota, leave or billing on its own.
            </p>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44] mb-5"
            data-testid="auto-disclaimer">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
            <div>
              These insights are <strong>read-only suggestions</strong>. NextRota never applies them on
              your behalf, every decision is still yours.
            </div>
          </div>

          <div id="readiness" className="mb-5">
            <ReadinessReport />
          </div>

          <div id="conflicts" className="mb-5">
            <ConflictReport />
          </div>
        </>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 h-10 p-1 mb-4 flex-wrap"
          data-testid="auto-tabs">
          {SURFACES.map((s) => (
            <TabsTrigger key={s.value} value={s.value}
              data-testid={`auto-tab-${s.value}`}
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] rounded-md px-3">
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="hub">
          <SimulationHub onNavigateToKind={(v) => setTab(v)} />
        </TabsContent>
        <TabsContent value="overview">
          <PreviewRunner
            path="/automation/preview/overview-summary"
            initialPayload={{ limit: 10 }}
            testidPrefix="auto-overview"
          />
        </TabsContent>
        <TabsContent value="coverage_sim">
          <CoverageSimulationTab />
        </TabsContent>
        <TabsContent value="leave_sim">
          <LeaveSimulationTab />
        </TabsContent>
        <TabsContent value="fairness_sim">
          <FairnessSimulationTab />
        </TabsContent>
        <TabsContent value="rota_day">
          <RotaDayPanel />
        </TabsContent>
        <TabsContent value="leave">
          <LeavePanel employees={employees} />
        </TabsContent>
        <TabsContent value="eligibility">
          <EligibilityPanel employees={employees} />
        </TabsContent>
        <TabsContent value="policy">
          <PolicyHealthPanel />
        </TabsContent>
        <TabsContent value="history">
          <RunHistoryPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PreviewRunner({ path, initialPayload, testidPrefix, renderInputs }) {
  const [payload, setPayload] = useState(initialPayload || {});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const update = (p) => setPayload((prev) => ({ ...prev, ...p }));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(path, payload);
      setResult(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  // Auto-run on first mount for surfaces that don't need inputs
  useEffect(() => { if (!renderInputs) run(); /* eslint-disable-next-line */ }, []);

  return (
    <Card>
      {renderInputs && (
        <div className="flex items-end gap-3 mb-4 flex-wrap">
          {renderInputs(payload, update)}
          <Button onClick={run} disabled={busy}
            data-testid={`${testidPrefix}-run`}
            className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Play className="h-4 w-4 mr-1" />{busy ? "Checking…" : "Check now"}
          </Button>
        </div>
      )}
      {result && (
        <div data-testid={`${testidPrefix}-result`}>
          <ResultHeader r={result} testidPrefix={testidPrefix} />
          <div className="space-y-2 mt-3">
            {result.recommendations.length === 0 && (
              <div className="py-8 text-center text-sm text-emerald-700"
                data-testid={`${testidPrefix}-empty`}>
                No issues found, everything looks tidy.
              </div>
            )}
            {result.recommendations.map((rec, i) => (
              <RecommendationCard key={i} rec={rec}
                testid={`${testidPrefix}-rec-${i}`} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function ResultHeader({ r, testidPrefix }) {
  const s = r.summary || {};
  const by = s.by_severity || {};
  return (
    <div className="flex items-center gap-3 flex-wrap pb-2 border-b border-slate-100">
      <div className="text-[11px] text-[#4B5773]">
        Run {r.run_id?.slice(-8)} · {r.duration_ms}ms
      </div>
      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
        data-testid={`${testidPrefix}-total`}>
        {s.total} {s.total === 1 ? "recommendation" : "recommendations"}
      </span>
      {by.critical > 0 && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
          {by.critical} critical
        </span>
      )}
      {by.warning > 0 && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          {by.warning} warning
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[11px] text-[#4B5773]">Overall confidence</span>
        <ConfidenceBar value={s.confidence ?? 1}
          testid={`${testidPrefix}-confidence`} />
      </div>
    </div>
  );
}

function RotaDayPanel() {
  const [payload, setPayload] = useState({ on_date: today(), team_id: null });
  return (
    <PreviewRunner
      path="/automation/preview/rota-day"
      initialPayload={payload}
      testidPrefix="auto-rota"
      renderInputs={(p, update) => (
        <>
          <div>
            <Label className="eyebrow">Date</Label>
            <Input type="date" value={p.on_date} data-testid="auto-rota-date"
              onChange={(e) => update({ on_date: e.target.value })}
              className="h-10 rounded-lg mt-2" />
          </div>
        </>
      )}
    />
  );
}

function LeavePanel({ employees }) {
  return (
    <PreviewRunner
      path="/automation/preview/leave-request"
      initialPayload={{
        employee_id: employees[0]?.employee_id || "",
        start_date: today(), end_date: inDays(4), leave_type: "annual",
      }}
      testidPrefix="auto-leave"
      renderInputs={(p, update) => (
        <>
          <div className="flex-1 min-w-[180px]">
            <Label className="eyebrow">Employee</Label>
            <Select value={p.employee_id}
              onValueChange={(v) => update({ employee_id: v })}>
              <SelectTrigger className="h-10 rounded-lg mt-2"
                data-testid="auto-leave-employee"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow">Start</Label>
            <Input type="date" value={p.start_date} data-testid="auto-leave-start"
              onChange={(e) => update({ start_date: e.target.value })}
              className="h-10 rounded-lg mt-2" />
          </div>
          <div>
            <Label className="eyebrow">End</Label>
            <Input type="date" value={p.end_date} data-testid="auto-leave-end"
              onChange={(e) => update({ end_date: e.target.value })}
              className="h-10 rounded-lg mt-2" />
          </div>
          <div>
            <Label className="eyebrow">Type</Label>
            <Select value={p.leave_type} onValueChange={(v) => update({ leave_type: v })}>
              <SelectTrigger className="h-10 rounded-lg mt-2"
                data-testid="auto-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["annual","sick","unpaid","bank_holiday","other"].map((t) =>
                  <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    />
  );
}

function EligibilityPanel({ employees }) {
  return (
    <PreviewRunner
      path="/automation/preview/employee-eligibility"
      initialPayload={{ employee_id: employees[0]?.employee_id || "", on_date: today() }}
      testidPrefix="auto-elig"
      renderInputs={(p, update) => (
        <>
          <div className="flex-1 min-w-[180px]">
            <Label className="eyebrow">Employee</Label>
            <Select value={p.employee_id}
              onValueChange={(v) => update({ employee_id: v })}>
              <SelectTrigger className="h-10 rounded-lg mt-2"
                data-testid="auto-elig-employee"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow">As of</Label>
            <Input type="date" value={p.on_date} data-testid="auto-elig-date"
              onChange={(e) => update({ on_date: e.target.value })}
              className="h-10 rounded-lg mt-2" />
          </div>
        </>
      )}
    />
  );
}

function PolicyHealthPanel() {
  return (
    <PreviewRunner
      path="/automation/preview/policy-health"
      initialPayload={{ window_days: 30 }}
      testidPrefix="auto-policy"
      renderInputs={(p, update) => (
        <>
          <div>
            <Label className="eyebrow">Window (days)</Label>
            <Input type="number" min="1" max="180"
              value={p.window_days} data-testid="auto-policy-window"
              onChange={(e) => update({ window_days: Number(e.target.value) })}
              className="h-10 rounded-lg mt-2 w-32" />
          </div>
        </>
      )}
    />
  );
}

function RunHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [surface, setSurface] = useState("all");
  useEffect(() => {
    const params = surface === "all" ? "" : `?surface=${surface}`;
    api.get(`/automation/runs${params}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [surface]);
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">Recent runs (latest 200)</div>
        <Select value={surface} onValueChange={setSurface}>
          <SelectTrigger className="h-9 rounded-lg w-44"
            data-testid="auto-history-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All surfaces</SelectItem>
            <SelectItem value="overview_summary">Overview</SelectItem>
            <SelectItem value="rota_day">Rota day</SelectItem>
            <SelectItem value="leave_request">Leave</SelectItem>
            <SelectItem value="employee_eligibility">Eligibility</SelectItem>
            <SelectItem value="policy_health">Policy health</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <table className="w-full text-sm" data-testid="auto-history-table">
        <thead>
          <tr className="text-left text-[#4B5773]">
            <th className="py-2 eyebrow font-normal">When</th>
            <th className="py-2 eyebrow font-normal">Surface</th>
            <th className="py-2 eyebrow font-normal">Found</th>
            <th className="py-2 eyebrow font-normal">Confidence</th>
            <th className="py-2 eyebrow font-normal">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <tr><td colSpan={5} className="py-8 text-center text-[#4B5773]"
                    data-testid="auto-history-empty">No runs yet.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.run_id} data-testid={`auto-history-row-${r.run_id}`}>
              <td className="py-2 text-[#4B5773]">
                {(r.triggered_at || "").slice(0, 19).replace("T", " ")}
              </td>
              <td className="py-2 text-[#4B5773] font-mono text-xs">{r.surface}</td>
              <td className="py-2">{r.result_summary?.recommendations_count ?? 0}</td>
              <td className="py-2">
                <ConfidenceBar value={r.result_summary?.overall_confidence ?? 1} />
              </td>
              <td className="py-2 text-[#4B5773]">{r.duration_ms}ms</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
